/**
 * Release Safety RS-8: one attemptId per lead-form INTENT, and no blind resubmit.
 *
 * - getOrCreateAttemptId(scope): the id is minted once per intent (e.g. one car's enquiry form) and
 *   kept in sessionStorage, so a retry, a reload or a re-open of the form in the same tab sends the
 *   SAME id and the server can recognise it. clearAttemptId(scope) after a confirmed success.
 * - submitWithResolution(): if the answer is lost (timeout, network error, gateway error) or the
 *   server says it is still processing, the outcome is looked up on the status endpoint instead of
 *   sending the form again.
 */
const STORAGE_PREFIX = 'leadAttempt:';
const memory = new Map();
const uuid = () => {
    const c = typeof crypto !== 'undefined' ? crypto : undefined;
    if (c && typeof c.randomUUID === 'function')
        return c.randomUUID();
    // fallback for very old browsers: RFC 4122 v4 shape from Math.random
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};
const readStorage = (key) => {
    try {
        return window.sessionStorage.getItem(key);
    }
    catch {
        return null;
    }
};
const writeStorage = (key, value) => {
    try {
        if (value === null)
            window.sessionStorage.removeItem(key);
        else
            window.sessionStorage.setItem(key, value);
    }
    catch {
        // storage blocked (private mode, quota): the in-memory copy still covers the current page
    }
};
export const getOrCreateAttemptId = (scope) => {
    const key = STORAGE_PREFIX + scope;
    const existing = readStorage(key) || memory.get(key);
    if (existing)
        return existing;
    const id = uuid();
    memory.set(key, id);
    writeStorage(key, id);
    return id;
};
export const clearAttemptId = (scope) => {
    const key = STORAGE_PREFIX + scope;
    memory.delete(key);
    writeStorage(key, null);
};
const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/**
 * BaseHttp rejects with the response BODY for an HTTP error, with the XMLHttpRequest when no
 * response arrived, and with a string for a setup error. Only the last two mean the request may
 * still have reached the server.
 */
export const isTransportLevelError = (error) => {
    if (typeof error === 'string')
        return true;
    return typeof XMLHttpRequest !== 'undefined' && error instanceof XMLHttpRequest;
};
const TIMED_OUT = Symbol('timed-out');
export class AttemptOutcomeUnknownError extends Error {
    constructor(attemptId, cause) {
        super(`The outcome of attempt ${attemptId} could not be determined`);
        Object.defineProperty(this, "attemptId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: attemptId
        });
        Object.defineProperty(this, "cause", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: cause
        });
        this.name = 'AttemptOutcomeUnknownError';
    }
}
/**
 * Runs `submit` and returns its result when the server confirms it. When the outcome is unknown,
 * asks `getStatus` (never the submit again). Throws the original error when the attempt failed for
 * sure, and AttemptOutcomeUnknownError when it could not be determined in the time allowed.
 */
export const submitWithResolution = async (attemptId, submit, getStatus, options = {}) => {
    const submitTimeoutMs = options.submitTimeoutMs ?? 30000;
    const pollIntervalMs = options.pollIntervalMs ?? 2000;
    const maxResolveMs = options.maxResolveMs ?? 60000;
    const sleep = options.sleep ?? defaultSleep;
    let originalError;
    let transportLevel = false;
    try {
        let timer;
        const timeout = new Promise((resolve) => {
            timer = setTimeout(() => resolve(TIMED_OUT), submitTimeoutMs);
        });
        const submitted = submit();
        // the submit keeps running if the timeout wins: the server deduplicates by attemptId
        submitted.catch(() => undefined);
        const result = await Promise.race([submitted, timeout]);
        if (timer)
            clearTimeout(timer);
        if (result === TIMED_OUT) {
            transportLevel = true;
        }
        else {
            const body = result;
            if (!body || body.status !== 'processing')
                return result;
            // the server accepted the attempt and is still publishing: resolve, do not resend
            transportLevel = true;
        }
    }
    catch (error) {
        originalError = error;
        transportLevel = isTransportLevelError(error);
    }
    // outcome unknown or failed: ask the server what happened to this attempt
    let waited = 0;
    let unknownAnswers = 0;
    for (;;) {
        let answer;
        try {
            answer = await getStatus(attemptId);
        }
        catch {
            answer = undefined;
        }
        const status = answer?.status;
        if (status === 'persisted')
            return answer;
        if (status === 'failed')
            throw originalError ?? new AttemptOutcomeUnknownError(attemptId);
        if (status === 'unknown' && !transportLevel) {
            // the server answered the submit with an error and has no record of the attempt: it failed
            throw originalError;
        }
        if (status === 'unknown')
            unknownAnswers += 1;
        if (waited >= maxResolveMs) {
            throw originalError ?? new AttemptOutcomeUnknownError(attemptId, unknownAnswers ? undefined : 'status endpoint unreachable');
        }
        await sleep(pollIntervalMs);
        waited += pollIntervalMs;
    }
};
